import styled, { css } from "styled-components";

export enum Variants {
    SOLID = "solid",
    OUTLINE = "outline",
    TEXT = "text",
}

export type ButtonStatus = "active" | "disabled" | "loading";

interface IButtonProps {
    variant?: Variants;
    size?: "sm" | "md";
    onlyHasIcon?: boolean;
    status?: ButtonStatus;
    fullWidth?: boolean;
    color: "primary" | "secondary";
    testId?: string;
    href?: string;
    target?: string;
    rel?: string;
}

const StyledButton = styled.button<IButtonProps>`
    align-items: center;
    background-color: ${(props) =>
        props.color === "primary"
            ? props.theme.colors.primary.primary400
            : props.theme.colors.secondary.secondary600};
    border-radius: ${(props) => props.theme.radius.sm};
    border: 0;
    color: ${(props) => props.theme.colors.neutral.white};
    cursor: pointer;
    display: flex;
    font-family: ${(props) => props.theme.typography.global.family};
    font-size: ${(props) => props.theme.typography.body.button.fontSize};
    justify-content: center;
    line-height: ${(props) => props.theme.typography.body.button.lineHeight};
    outline: none;
    overflow: hidden;
    padding: ${(props) => (props.size === "sm" ? "6px 16px" : "8px 22px")};
    position: relative;
    text-align: center;
    text-align: center;
    text-decoration: none;
    transition: color 0.2s, background-color 0.2s, box-shadow 0.2s;
    user-select: none;
    white-space: nowrap;

    &:hover {
        background-color: ${(props) =>
            props.color === "primary"
                ? props.theme.colors.primary.primary300
                : props.theme.colors.secondary.secondary500};
        box-shadow: ${(props) => props.theme.shadows.xs};
    }

    &:active {
        background-color: ${(props) =>
            props.color === "primary"
                ? props.theme.colors.primary.primary500
                : props.theme.colors.secondary.secondary700};
        box-shadow: none;
    }

    ${(props) =>
        props.variant === Variants.OUTLINE &&
        css`
            background: ${(props) => props.theme.colors.neutral.white};
            border: 1px solid ${(props) => props.theme.colors.secondary.secondary700};
            color: ${(props) => props.theme.colors.secondary.secondary700};

            &:hover {
                border: 1px solid ${(props) => props.theme.colors.secondary.secondary600};
                color: ${(props) => props.theme.colors.neutral.white};
            }

            &:active {
                background: ${(props) => props.theme.colors.secondary.secondary800};
                border: 1px solid ${(props) => props.theme.colors.secondary.secondary800};
                color: ${(props) => props.theme.colors.neutral.white};
            }
        `};

    ${(props) =>
        props.variant === Variants.TEXT &&
        css`
            background: ${(props) => props.theme.colors.neutral.white};
            color: ${(props) => props.theme.colors.secondary.secondary700};

            &:hover {
                background: ${(props) => props.theme.colors.neutral.white};
                color: ${(props) => props.theme.colors.secondary.secondary600};
            }

            &:active {
                background: ${(props) => props.theme.colors.neutral.neutral50};
                color: ${(props) => props.theme.colors.secondary.secondary800};
            }
        `};

    ${(props) =>
        props.status === "disabled" &&
        css`
            background: ${(props) => props.theme.colors.neutral.neutral50};
            border: 1px solid ${(props) => props.theme.colors.neutral.neutral50};
            color: ${(props) => props.theme.colors.neutral.neutral100};
            cursor: not-allowed;

            &:hover,
            &:active {
                border: 1px solid ${(props) => props.theme.colors.neutral.neutral50};
                background: ${(props) => props.theme.colors.neutral.neutral50};
                box-shadow: none;
                color: ${(props) => props.theme.colors.neutral.neutral100};
                border: ${props.variant === Variants.TEXT ? 0 : undefined};
            }
        `};

    ${(props) =>
        props.status === "loading" &&
        css`
            background: ${(props) => props.theme.colors.neutral.neutral50};
            border: 1px solid ${(props) => props.theme.colors.neutral.neutral50};
            color: ${(props) => props.theme.colors.neutral.neutral100};
            cursor: not-allowed;

            &:hover,
            &:active {
                border: 1px solid ${(props) => props.theme.colors.neutral.neutral50};
                background: ${(props) => props.theme.colors.neutral.neutral50};
                box-shadow: none;
                color: ${(props) => props.theme.colors.neutral.neutral100};
                border: ${props.variant === Variants.TEXT ? 0 : undefined};
            }
        `};

    ${(props) =>
        props.onlyHasIcon &&
        css`
            border-radius: 50%;
            height: ${props.size === "sm" ? props.theme.spacing.xl : props.theme.spacing.xxl};
            padding: ${props.size === "sm" ? props.theme.spacing.xxs : props.theme.spacing.xxs};
            width: ${props.size === "sm" ? props.theme.spacing.xl : props.theme.spacing.xxl};
            svg {
                display: block;
                margin: 5px;
            }
        `}

    ${(props) =>
        props.fullWidth &&
        css`
      display: block;
      width: 100%;
      }
  `}
`;

export default StyledButton;
